import React, { useSyncExternalStore } from 'react';
import { Scale } from 'lucide-react';
import { PACK_SIZE } from '../../utils/warehouseStock';
import { formatBagKg, formatKg } from '../../utils/bagWeight';

// Un único ajuste "Mostrar peso" para todas las pantallas; se guarda en este navegador.
const STORAGE_KEY = 'polcen.showBagWeight';
const listeners = new Set<() => void>();

const read = () => {
  try { return localStorage.getItem(STORAGE_KEY) === '1'; } catch { return false; }
};
let current = read();

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
};

export const setShowWeight = (value: boolean) => {
  current = value;
  try { localStorage.setItem(STORAGE_KEY, value ? '1' : '0'); } catch { /* sin almacenamiento: vale solo para esta sesión */ }
  listeners.forEach((listener) => listener());
};

export const useShowWeight = () => useSyncExternalStore(subscribe, () => current, () => false);

const WeightToggle: React.FC<{ className?: string }> = ({ className = '' }) => {
  const show = useShowWeight();
  return (
    <button
      type="button"
      onClick={() => setShowWeight(!show)}
      aria-pressed={show}
      title="Peso estimado por bolsa: ancho × largo × micrones × 2 × 0,92 ÷ 10.000. Se aplica en todas las pantallas."
      className={`inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-xs font-medium ${show ? 'border-primary-300 bg-primary-50 text-primary-700' : 'bg-white text-gray-600 hover:bg-gray-50'} ${className}`}
    >
      <Scale className="h-3.5 w-3.5" />{show ? 'Ocultar peso' : 'Mostrar peso'}
    </button>
  );
};

// Peso estimado de una bolsa y, debajo, de un pack completo.
export const BagWeight: React.FC<{ grams: number | null; units?: number }> = ({ grams, units }) => {
  if (grams == null) return <span className="text-gray-400" title="Falta el micronaje para calcular el peso">-</span>;
  return (
    <span className="whitespace-nowrap">
      {formatBagKg(grams)}
      <span className="block text-[10px] text-gray-500">{formatKg(grams * (Number(units) || PACK_SIZE) / 1000)} el pack</span>
    </span>
  );
};

export default WeightToggle;
